<template>
    <div v-if="isOpen" class="modal sp-modal-card is-active">
        <div class="modal-background" @click="cancel"></div>
        <div class="modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title" :title="event?.id">Edit Session</p>
                <button class="delete" @click="cancel"></button>
            </header>
            <section class="modal-card-body">
                <div class="field">
                    <label class="label">Timer</label>
                    <div class="control">
                        <input class="input" type="text" :value="timerName" readonly />
                    </div>
                </div>

                <div class="columns">
                    <div class="column">
                        <TimePicker 
                            v-model="editStartTime"
                            label="Start Time"
                            :max-date="new Date()"
                            :min-date="sessionStartTime"
                            :error="startTimeError"
                        />
                    </div>
                    <div class="column">
                        <TimePicker 
                            v-model="editEndTime"
                            label="End Time"
                            :max-date="new Date()"
                            :min-date="editStartTime"
                            :error="endTimeError"
                        />
                    </div>
                </div>

                <div class="field">
                    <label class="label">Duration</label>
                    <div class="control">
                        <input class="input" type="text" :value="durationPreview" readonly />
                    </div>
                    <p class="help">Duration will be automatically calculated from start and end times</p>
                </div>

                <!-- Session boundary help -->
                <div class="notification p-3 block">
                    <p class="is-size-7">
                        <strong>Note:</strong> Times must be within today's session 
                        ({{ formatTime(sessionStartTime) }} - {{ formatTime(new Date()) }})
                    </p>
                </div>

                <!-- Error display -->
                <div v-if="errorMessage" class="notification p-3 is-danger">
                    {{ errorMessage }}
                </div>
            </section>
            <footer class="modal-card-foot">
                <div class="buttons">
                    <button
                        class="button is-primary"
                        @click="save"
                        :disabled="!isValidTimeRange || isSaving"
                        :class="{ 'is-loading': isSaving }"
                    >
                        Save
                    </button>
                    <button class="button" @click="cancel" :disabled="isSaving">Cancel</button>
                    <button 
                        class="button is-danger is-outlined" 
                        @click="deleteEvent" 
                        :disabled="isSaving"
                    >
                        Delete Session
                    </button>
                </div>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import TimePicker from './TimePicker.vue';
import { formatTime, formatDuration } from '../utils/time';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    event: {
        type: Object,
        default: null
    },
    timerName: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['save', 'cancel', 'delete']);

// Form state
const editStartTime = ref('');
const editEndTime = ref('');
const startTimeError = ref('');
const endTimeError = ref('');
const errorMessage = ref('');
const isSaving = ref(false);

// Session boundaries
const sessionStartTime = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
});

// Validation
const isValidTimeRange = computed(() => {
    if (!editStartTime.value || !editEndTime.value) return false;
    
    const startTime = new Date(editStartTime.value);
    const endTime = new Date(editEndTime.value);
    
    return startTime < endTime;
});

// Duration preview
const durationPreview = computed(() => {
    if (!isValidTimeRange.value) return '—';
    
    const startTime = new Date(editStartTime.value);
    const endTime = new Date(editEndTime.value);
    const durationSeconds = Math.floor((endTime - startTime) / 1000);
    
    return formatDuration(durationSeconds);
});

// Significant change detection (client-side heuristic)
const showSignificantChangeWarning = computed(() => {
    if (!props.event || !editStartTime.value || !editEndTime.value) return false;
    
    const originalStart = new Date(props.event.started_at);
    const originalEnd = new Date(props.event.ended_at);
    const newStart = new Date(editStartTime.value);
    const newEnd = new Date(editEndTime.value);
    
    // Calculate time shifts (in minutes)
    const startShift = Math.abs((newStart - originalStart) / 1000 / 60);
    const endShift = Math.abs((newEnd - originalEnd) / 1000 / 60);
    
    // Calculate duration change percentage
    const originalDuration = (originalEnd - originalStart) / 1000;
    const newDuration = (newEnd - newStart) / 1000;
    const durationChangePercent = Math.abs((newDuration - originalDuration) / originalDuration) * 100;
    
    // Significant if time shifts by 10+ minutes OR duration changes by 25%+
    return startShift >= 10 || endShift >= 10 || durationChangePercent >= 25;
});

const getChangeDescription = () => {
    if (!props.event || !editStartTime.value || !editEndTime.value) return '';

    const originalStart = new Date(props.event.started_at);
    const originalEnd = new Date(props.event.ended_at);
    const newStart = new Date(editStartTime.value);
    const newEnd = new Date(editEndTime.value);

    const startShift = Math.abs((newStart - originalStart) / 1000 / 60);
    const endShift = Math.abs((newEnd - originalEnd) / 1000 / 60);

    const originalDuration = (originalEnd - originalStart) / 1000;
    const newDuration = (newEnd - newStart) / 1000;
    const durationChangePercent = Math.abs((newDuration - originalDuration) / originalDuration) * 100;

    const parts = [];
    if (startShift >= 10) parts.push(`start time shift: ${Math.round(startShift)} min`);
    if (endShift >= 10) parts.push(`end time shift: ${Math.round(endShift)} min`);
    if (durationChangePercent >= 25) parts.push(`duration change: ${Math.round(durationChangePercent)}%`);

    return parts.join(', ');
};

// Initialize form when event changes
watch(() => props.event, (newEvent) => {
    if (newEvent) {
        editStartTime.value = newEvent.started_at;
        editEndTime.value = newEvent.ended_at;
        clearErrors();
    }
}, { immediate: true });

// Clear form when modal closes
watch(() => props.isOpen, (isOpen) => {
    if (!isOpen) {
        clearForm();
    }
});

const clearForm = () => {
    editStartTime.value = '';
    editEndTime.value = '';
    clearErrors();
    isSaving.value = false;
};

const clearErrors = () => {
    startTimeError.value = '';
    endTimeError.value = '';
    errorMessage.value = '';
};

const save = async () => {
    clearErrors();
    
    // Validate inputs
    if (!editStartTime.value) {
        startTimeError.value = 'Start time is required';
        return;
    }
    
    if (!editEndTime.value) {
        endTimeError.value = 'End time is required';
        return;
    }
    
    if (!isValidTimeRange.value) {
        endTimeError.value = 'End time must be after start time';
        return;
    }
    
    // Validate session boundaries
    const startTime = new Date(editStartTime.value);
    const endTime = new Date(editEndTime.value);
    const now = new Date();
    const sessionStart = sessionStartTime.value;
    
    if (startTime > now || endTime > now) {
        errorMessage.value = 'Times cannot be in the future';
        return;
    }
    
    if (startTime < sessionStart || endTime < sessionStart) {
        errorMessage.value = 'Times must be within today\'s session';
        return;
    }
    
    const saveData = {
        started_at: editStartTime.value,
        ended_at: editEndTime.value
    };
    
    // If significant change, show JavaScript confirmation
    if (showSignificantChangeWarning.value) {
        const confirmed = confirm(`You are making a significant change to this session (${getChangeDescription()}). Are you sure you want to continue?`);
        if (!confirmed) {
            return;
        }
    }
    
    // Proceed with save
    await performSave(saveData, showSignificantChangeWarning.value);
};

const performSave = async (saveData, force) => {
    isSaving.value = true;
    
    try {
        emit('save', {
            eventId: props.event.id,
            data: saveData,
            force
        });
    } catch (error) {
        console.error('Error saving session:', error);
        errorMessage.value = 'An error occurred while saving';
    } finally {
        isSaving.value = false;
    }
};

const cancel = () => {
    emit('cancel');
};

const deleteEvent = () => {
    if (confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
        emit('delete', props.event.id);
    }
};
</script>

<style scoped>
/* Match TimerModal spacing / overflow behavior */
.modal-card-body {
    overflow: visible;
}
</style>